import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, ShieldCheck, Clock, RefreshCcw, AlertCircle, HelpCircle } from "lucide-react";

const STATUS_INFO = {
  VERIFIED: { label: "Verified", tone: "#16a34a", Icon: ShieldCheck },
  SUBMITTED: { label: "Submitted", tone: "#0891b2", Icon: Clock },
  REVISION_REQUIRED: { label: "Revisi", tone: "#f97316", Icon: RefreshCcw },
  PARTIAL: { label: "Partial", tone: "#8b5cf6", Icon: AlertCircle },
  UNVERIFIED: { label: "Unverified", tone: "#6b7280", Icon: HelpCircle },
};

export default function VerifikasiData() {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/churches");
      setChurches(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const jemaats = churches.filter((c) => (c.type || "JEMAAT") === "JEMAAT");
  const grouped = Object.keys(STATUS_INFO).map((status) => ({
    status,
    label: STATUS_INFO[status].label,
    tone: STATUS_INFO[status].tone,
    value: jemaats.filter((c) => (c.verification_status || "UNVERIFIED") === status).length,
    items: jemaats.filter((c) => (c.verification_status || "UNVERIFIED") === status),
  }));

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="verifikasi-data-root">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/jemaat")} className="mb-2" data-testid="btn-back-jemaat">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Kembali ke Daftar Jemaat
          </Button>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">Database Jemaat</div>
          <h1 className="font-heading font-black text-3xl mt-1">Verifikasi Data Jemaat</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Ringkasan status verifikasi profil 8 Jemaat. Grafik ini sebelumnya di Dashboard utama; dipindahkan agar Dashboard fokus pada perkembangan pelayanan.</p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Distribusi Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-72">
              <ResponsiveContainer>
                <BarChart data={grouped} margin={{ top: 16, right: 12, left: -12, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={38} />
                  <Tooltip cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-6">Memuat…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped.map((g) => {
              const Icon = STATUS_INFO[g.status].Icon;
              return (
                <Card key={g.status} data-testid={`card-status-${g.status.toLowerCase()}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" style={{color: g.tone}} />
                      <div className="text-xs uppercase tracking-widest font-semibold">{g.label}</div>
                      <Badge className="ml-auto" style={{backgroundColor: g.tone, color: '#fff'}}>{g.value}</Badge>
                    </div>
                    <div className="mt-2 space-y-1">
                      {g.items.length === 0 ? (
                        <div className="text-xs text-muted-foreground italic">Tidak ada jemaat pada status ini.</div>
                      ) : g.items.map((c) => (
                        <Link key={c.id} to={`/jemaat/${c.id}`} className="block text-sm hover:underline">
                          {c.name.replace("GPDP Jemaat ", "")}
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
